// Shared date formatting. Keeps date presentation consistent and locale-aware.
//
// Every locale renders a two-digit year — uk as dd.mm.yy ("09.06.26"), others in
// their own day/month order with the same two-digit year. Pass `locale` to
// override; otherwise the current i18n language is used.
//
// UI-02 — the two-digit year used to be a uk-only branch, and every other locale
// fell through to `toLocaleDateString(locale)` with no options, yielding a
// four-digit year. Collapsing the branch is deliberately a change to the shared
// utility rather than to one call site: this is the reason the utility exists,
// and it reaches every date the web client formats.

import type { Locale } from 'date-fns';
import { enUS, uk } from 'date-fns/locale';
import i18n from 'i18n';

function resolveLocale(locale?: string): string {
  return (locale ?? i18n.language ?? 'uk').split('-')[0].toLowerCase();
}

/** Format a date as a short calendar date with a two-digit year (dd.mm.yy on Ukrainian). */
export function formatDate(value: Date | string | number | null | undefined, locale?: string): string {
  if (value == null) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const lng = resolveLocale(locale);
  return d.toLocaleDateString(lng === 'uk' ? 'uk' : (locale ?? lng), { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Format a date as a short calendar date plus time of day (dd.mm.yy hh:mm on Ukrainian). */
export function formatDateTime(value: Date | string | number | null | undefined, locale?: string): string {
  if (value == null) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const lng = resolveLocale(locale);
  const time = d.toLocaleTimeString(lng === 'uk' ? 'uk' : (locale ?? lng), { hour: '2-digit', minute: '2-digit' });
  return `${formatDate(d, locale)} ${time}`;
}

/** Format a time of day on its own (hh:mm). */
export function formatTime(value: Date | string | number | null | undefined, locale?: string): string {
  if (value == null) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const lng = resolveLocale(locale);
  return d.toLocaleTimeString(lng === 'uk' ? 'uk' : (locale ?? lng), { hour: '2-digit', minute: '2-digit' });
}

/**
 * A rent window with the time of day at both ends (TASK-02): `07.10.26 08:00 → 18:00` when both ends fall on the
 * same local day, `05.10.26 09:00 → 07.10.26 17:00` otherwise. A missing end reads `—`.
 */
export function formatDateTimeRange(
  start: Date | string | number | null | undefined,
  end: Date | string | number | null | undefined,
  locale?: string
): string {
  const from = formatDateTime(start, locale);
  if (!from) return '';
  const to = end == null ? '' : new Date(end);
  if (!to || Number.isNaN(to.getTime())) return `${from} → —`;
  const s = new Date(start as Date | string | number);
  const sameDay = s.getFullYear() === to.getFullYear() && s.getMonth() === to.getMonth() && s.getDate() === to.getDate();
  return `${from} → ${sameDay ? formatTime(to, locale) : formatDateTime(to, locale)}`;
}

/**
 * VEK-190 (UI-22) — the sectioned-field format of a whole-hour date-time picker: the day/month order
 * and two-digit year `formatDate` prints for the language, hours with a static `:00` (no minute
 * section to edit at all). `26.10.26 09:00` on Ukrainian, `10/26/26 09:00` otherwise.
 */
export function hourPickerFormat(locale?: string): string {
  return resolveLocale(locale) === 'uk' ? "dd.MM.yy HH:'00'" : "MM/dd/yy HH:'00'";
}

/** VEK-190 (UI-22) — the date-fns locale behind a picker, so its popup's month and weekday names follow the UI language. */
export function dateFnsLocale(locale?: string): Locale {
  return resolveLocale(locale) === 'uk' ? uk : enUS;
}
